import type { CSSProperties } from "react";
import { cx } from "./cx";

/** A steady hue for a name, so a person keeps the same colour wherever their initials show. */
export function hueOf(name: string): number {
  let h = 0;
  for (const ch of name) h = (h * 31 + ch.charCodeAt(0)) % 360;
  return h;
}

/** Up to two letters from a name: "Priya Raman" is "PR". */
export function initialsOf(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}

interface FaceProps {
  name: string;
  /** Their photo; without one the face is their initials on their own hue. */
  photo?: string | null;
  size?: number;
  className?: string;
  style?: CSSProperties;
}

/** A person's face (`.face`): their photo, or their initials. */
export function Face({ name, photo, size = 36, className, style }: FaceProps) {
  const vars = { "--s": `${size}px`, ...(photo ? {} : { "--h": hueOf(name) }), ...style } as CSSProperties;
  if (photo) {
    return (
      <span className={cx("face", className)} style={vars} title={name}>
        <img src={photo} alt={name} />
      </span>
    );
  }
  return (
    <span className={cx("face ini", className)} style={vars} title={name} role="img" aria-label={name}>
      {initialsOf(name)}
    </span>
  );
}

/** Faces overlapping in a row (`.stack`), with "+N" when there are more people than faces shown. */
export function FaceStack({ people, size = 28, more = 0, className }: {
  people: ReadonlyArray<{ name: string; photo?: string | null }>;
  size?: number;
  more?: number;
  className?: string;
}) {
  return (
    <span className={cx("stack", className)} style={{ "--s": `${size}px` } as CSSProperties}>
      {people.map((p, i) => (
        <Face key={`${p.name}-${i}`} name={p.name} photo={p.photo} size={size} style={{ zIndex: people.length - i + 1 }} />
      ))}
      {more > 0 ? (
        <span className="face more" style={{ "--s": `${size}px` } as CSSProperties}>
          +{more.toLocaleString("en-US")}
        </span>
      ) : null}
    </span>
  );
}

/**
 * A face inside a ring (`.ring`) that fills with how far they are through a talk: green while they watch now
 * (with a live dot), green when finished, wine otherwise.
 */
export function Ring({ name, photo, size = 36, percent, live = false, brass = false }: FaceProps & {
  percent: number;
  live?: boolean;
  brass?: boolean;
}) {
  const w = size + 10;
  const r = w / 2 - 1.6;
  const c = 2 * Math.PI * r;
  const done = Math.max(0, Math.min(percent, 100));
  return (
    <span className={cx("ring", live ? "live" : done >= 100 ? "done" : brass && "brassr")} style={{ "--w": `${w}px` } as CSSProperties}>
      <svg viewBox={`0 0 ${w} ${w}`} aria-hidden="true">
        <circle className="rt" cx={w / 2} cy={w / 2} r={r} />
        <circle
          className="rp"
          cx={w / 2}
          cy={w / 2}
          r={r}
          strokeDasharray={`${((c * done) / 100).toFixed(1)} ${c.toFixed(1)}`}
          transform={`rotate(-90 ${w / 2} ${w / 2})`}
        />
      </svg>
      <Face name={name} photo={photo} size={size} />
      {live ? <i className="livedot" /> : null}
    </span>
  );
}
